import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';

import { ButtonModule } from 'primeng/button';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SharedModule } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { select, Store } from '@ngrx/store';
import { DialogService } from 'primeng/dynamicdialog';
import { TooltipModule } from 'primeng/tooltip';
import { AgGridModule } from 'ag-grid-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  CellClickedEvent,
  ColDef,
  FilterModel,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridState,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowSelectionOptions,
  StateUpdatedEvent,
} from 'ag-grid-community';
import { SplitButtonModule } from 'primeng/splitbutton';
import { GetRowIdParams } from 'ag-grid-enterprise';
import { ApplicationEvent, CoaterLicense } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { GlobalStateActions, selectGridStates, selectUser } from '@workspace-app/state/global';
import { PermissionService, QCT_ROLES } from '@workspace-app/shared/services/permission.service';
import { PageComponent, setColumnDefsPreservingState, ToastService, User } from '@workspace-app/shared';
import { TimelineDialogComponent } from '@workspace-app/shared/timeline/components/timeline-dialog/timeline-dialog.component';
import {
  ActionMenuItem,
  ActionsMenuComponent,
} from '@workspace-app/shared/components/actions-menu/actions-menu.component';
import { GlDataService } from '@workspace-app/crm/shared/data-services/gl-data.service';
import { getLicensesColumns } from './licenses.columns';
import { LicenseDataService } from './license-data.service';
import { LicenseTimelineService } from '../../services/license-timeline.service';
import { LicenseOverlayComponent } from '../license-view/license-overlay.component';
import { LicensesService } from '../../services/licenses.service';
import { LicenseAuthorizationService } from '../../services/license-authorization.service';
import { LicenseBulkDownloadDialogComponent } from '../../components/bulk-download-dialog/bulk-download-dialog.component';
import { ShareCertificatesDialogComponent } from '../../../shared/certificate-sharing/share-certificates-dialog/share-certificates-dialog.component';

@Component({
  selector: 'app-licenses',
  imports: [
    ButtonModule,
    FontAwesomeModule,
    PageComponent,
    SharedModule,
    TableModule,
    TooltipModule,
    AgGridModule,
    SplitButtonModule,
    ActionsMenuComponent,
  ],
  providers: [DialogService],
  templateUrl: './licenses.component.html',
  styleUrls: ['./licenses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LicensesComponent {
  private store = inject(Store);
  private dialogService = inject(DialogService);
  private licenseDataService = inject(LicenseDataService);
  private glDataService = inject(GlDataService);
  private toastService = inject(ToastService);
  private permissionService = inject(PermissionService);
  private licenseTimelineService = inject(LicenseTimelineService);
  private licensesService = inject(LicensesService);
  private router = inject(Router);
  private licenseAuthService = inject(LicenseAuthorizationService);

  colDefs: ColDef[] = [];

  // Quick filter signals
  private activeRenewalFilter = signal<string | null>(null);
  private user = signal<User | undefined>(undefined);

  // Grid state persistence
  private stateUpdateTimeout?: number;
  private preservedFilterModel: FilterModel | null = null;
  private isInitializing = true;
  private isFirstDataRendered = false;

  // Computed signals for each filter button state
  inProgressFilter = computed(() => this.activeRenewalFilter() === 'In Progress');
  certificateReadyFilter = computed(() => this.activeRenewalFilter() === 'Certificate Ready');

  // Permission check for license renewal workflow access (License.Admin only)
  canManageRenewalWorkflows = computed(() => {
    return this.licenseAuthService.canManageRenewalWorkflows(this.user() ?? null);
  });

  // "Share Certificates" / per-coater download are open to QCT and GL users (a GL only sees its own
  // coaters — enforced by the API)
  canShareCertificates = computed(() => this.permissionService.hasAnyEffectiveRole(this.user(), [...QCT_ROLES, 'GL']));

  // Actions menu items - computed to react to user permission changes
  actionsMenuItems = computed<ActionMenuItem[]>(() => [
    {
      label: 'Create License',
      icon: 'far fa-plus',
      command: () => this.router.navigate(['/certification/licenses/new']),
      visible: this.canManageRenewalWorkflows(),
    },
    {
      label: 'License Renewals',
      icon: 'far fa-calendar-check',
      command: () => this.navigateToLicenseRenewals(),
      visible: this.canManageRenewalWorkflows(),
    },
    {
      label: 'Bulk Download Certificates',
      icon: 'far fa-download',
      command: () => this.openBulkDownloadDialog(),
      visible: this.canManageRenewalWorkflows() || this.canShareCertificates(),
    },
    {
      label: 'Share Certificates',
      icon: 'far fa-share-nodes',
      command: () => this.openShareCertificatesDialog(),
      visible: this.canShareCertificates(),
    },
  ]);

  // Check if any action menu items are visible
  hasVisibleActions = computed(() => this.actionsMenuItems().some((item) => item.visible));

  gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
      enablePivot: false,
    },
    rowModelType: 'serverSide',
    pivotMode: false,
    columnMenu: 'new',
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivots: true,
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressValues: true,
          },
        },
      ],
    },
    rowHeight: 42,
    pagination: true,
    paginationPageSize: 50,
    cacheBlockSize: 50,
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 100,
    },
    context: this,
    getRowId: this.getRowId,
  };

  public rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
  };

  initialGridState = {} as GridState;
  private gridApi?: GridApi;
  private gridKey = 'licenseNumbersGrid';

  destroyRef = inject(DestroyRef);

  constructor() {
    this.store.pipe(select(selectUser), takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.user.set(user);
      const colDefs = getLicensesColumns(
        {
          openTimeline: (license: CoaterLicense) => this.openTimeline(license),
        },
        {
          glNamesFetcher: (params) =>
            this.glDataService.fetchGlNamesForFilter().subscribe((names) => params.success(names)),
        }
      );
      // Refresh columns without reassigning this.colDefs: the [columnDefs] binding would
      // force-reapply the defs and reset the user's column layout (see setColumnDefsPreservingState).
      if (this.gridApi) {
        setColumnDefsPreservingState(this.gridApi, colDefs);
      } else {
        this.colDefs = colDefs;
      }
    });

    // Subscribe to reload trigger from the service
    this.licensesService.reloadTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reloadGridData());

    // Subscribe to grid states for initial state
    this.store.pipe(select(selectGridStates), takeUntilDestroyed(this.destroyRef)).subscribe((gridStates) => {
      if (gridStates && gridStates[this.gridKey]) {
        this.initialGridState = { ...gridStates[this.gridKey] };
      } else {
        this.initialGridState = {} as GridState;
      }
    });

    // Cleanup timeout on component destruction
    this.destroyRef.onDestroy(() => {
      clearTimeout(this.stateUpdateTimeout);
    });
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;

    // Refresh columns on grid ready
    this.store.pipe(select(selectUser), takeUntilDestroyed(this.destroyRef)).subscribe((_user) => {
      const newColDefs = getLicensesColumns(
        {
          openTimeline: (license: CoaterLicense) => this.openTimeline(license),
        },
        {
          glNamesFetcher: (params) =>
            this.glDataService.fetchGlNamesForFilter().subscribe((names) => params.success(names)),
        }
      );
      if (this.gridApi) {
        setColumnDefsPreservingState(this.gridApi, newColDefs);
      }
    });

    const dataSource: IServerSideDatasource = {
      getRows: (params: IServerSideGetRowsParams) => {
        this.licenseDataService.getRows(params);
      },
    };

    // Preserve the ENTIRE filter model from the restored grid state BEFORE setting datasource
    this.preservedFilterModel = this.gridApi?.getFilterModel() || null;

    // Set the datasource immediately - filter restoration will happen in firstDataRendered
    this.gridApi.setGridOption('serverSideDatasource', dataSource);
  }

  getRowId(params: GetRowIdParams): string {
    return params.data.id;
  }

  onFirstDataRendered(_event: FirstDataRenderedEvent) {
    this.isFirstDataRendered = true;

    // Restore filter state after first data is rendered to ensure proper timing
    if (this.preservedFilterModel && Object.keys(this.preservedFilterModel).length > 0) {
      // Use a small timeout to ensure ag-grid is fully ready to accept filter changes
      setTimeout(() => {
        this.gridApi?.setFilterModel(this.preservedFilterModel);
        this.updateQuickFiltersFromModel(this.preservedFilterModel);
      }, 100);
    } else {
      // No preserved filters, ensure quick filters are cleared
      this.updateQuickFiltersFromModel({});
    }

    // Mark initialization as complete after filter restoration
    setTimeout(() => {
      this.isInitializing = false;
    }, 200);
  }

  private updateQuickFiltersFromModel(filterModel: FilterModel | null) {
    const filterObj = filterModel as Record<string, unknown> | null;
    const statusFilter = filterObj?.['renewalDisplayStatus'] as { values?: string[] } | undefined;
    if (statusFilter && statusFilter.values && statusFilter.values.length > 0) {
      // Check for "In Progress" filter
      if (statusFilter.values.includes('In Progress')) {
        this.activeRenewalFilter.set('In Progress');
      }
      // Check for "Certificate Ready" or "Certificate Sent" filter
      else if (statusFilter.values.includes('Certificate Ready') || statusFilter.values.includes('Certificate Sent')) {
        this.activeRenewalFilter.set('Certificate Ready');
      } else {
        this.activeRenewalFilter.set(null);
      }
    } else {
      this.activeRenewalFilter.set(null);
    }
  }

  stateUpdated($event: StateUpdatedEvent) {
    // Skip state updates during initialization phase to prevent race conditions
    if (this.isInitializing || !this.isFirstDataRendered) {
      return;
    }

    // Skip initialization-related events that can cause race conditions
    const initializationSources = ['gridInitializing', 'columnResizing', 'sorting', 'columnMoved', 'columnVisible'];
    if ($event.sources && $event.sources.some((source) => initializationSources.includes(source))) {
      return;
    }

    // Debounce rapid-fire state updates to avoid race conditions
    clearTimeout(this.stateUpdateTimeout);
    this.stateUpdateTimeout = window.setTimeout(() => {
      this.processStateUpdate($event);
    }, 100);
  }

  private processStateUpdate($event: StateUpdatedEvent) {
    // Get current filter model and update quick filter states
    const currentFilterModel = this.gridApi?.getFilterModel();
    this.updateQuickFiltersFromModel(currentFilterModel || null);

    // Store the current grid state
    const gridState = $event.api?.getState();
    this.store.dispatch(GlobalStateActions.storeGridState({ key: this.gridKey, gridState }));
  }

  resetTableState() {
    // Reset quick filter signals
    this.activeRenewalFilter.set(null);
    // Reset filters only — the user's column layout (visibility, order, widths) is kept
    this.gridApi?.setFilterModel(null);
  }

  navigateToRenewalReviews() {
    this.router.navigate(['/certification/licenses/extension-reviews']);
  }

  adjustColumnSizes() {
    this.gridApi?.sizeColumnsToFit();
  }

  // Note: withdraw and grant methods have been moved to the license view component
  // These actions are now only available from within the individual license view

  onCellClicked($event: CellClickedEvent) {
    const headerName = $event.column.getColDef().headerName;
    if (headerName !== 'Actions') {
      const license = $event.data as CoaterLicense;
      this.view(license);
    }
  }

  view(license: CoaterLicense) {
    this.dialogService.open(LicenseOverlayComponent, {
      header: `License ${license.completeLicenseNumber}`,
      position: 'right',
      height: '100%',
      closable: true,
      draggable: false,
      maskStyleClass: 'mask',
      modal: true,
      baseZIndex: 1000, // Lower than PDF viewer's 10000
      contentStyle: { overflow: 'auto' },
      data: {
        license,
      },
    });

    // Note: Grid reload is handled by the LicensesService.reloadTrigger subscription
    // No need to listen to onClose here
  }

  private reloadGridData(): void {
    if (!this.gridApi) return;

    // For server-side row model, purge cache and refresh to reload from server
    // purge: true ensures we get fresh data with all updated properties
    this.gridApi.refreshServerSide({ purge: true });
  }

  filterRenewalStatus(status: string) {
    if (this.activeRenewalFilter() === status) {
      // If clicking the same filter, clear it
      this.clearRenewalFilters();
    } else {
      this.activeRenewalFilter.set(status);
      // Apply filter to grid using the filter model approach
      const filterModel = this.gridApi?.getFilterModel() || ({} as Record<string, unknown>);

      // For Certificate Ready, include both "Certificate Ready" and "Certificate Sent" (in case of cached data)
      if (status === 'Certificate Ready') {
        filterModel['renewalDisplayStatus'] = {
          filterType: 'set',
          values: ['Certificate Ready', 'Certificate Sent'],
        };
      } else {
        filterModel['renewalDisplayStatus'] = {
          filterType: 'set',
          values: [status],
        };
      }
      this.gridApi?.setFilterModel(filterModel);
    }
  }

  clearRenewalFilters() {
    this.activeRenewalFilter.set(null);
    // Clear filter in grid
    const filterModel = this.gridApi?.getFilterModel() || ({} as Record<string, unknown>);
    delete filterModel['renewalDisplayStatus'];
    this.gridApi?.setFilterModel(filterModel);
  }

  openTimeline(license: CoaterLicense) {
    if (!license) return;

    // Custom icon mapper for license-specific events
    const iconMapper = (eventType: string, _eventData?: unknown) => {
      // Specific icons for license events
      if (eventType === 'license.withdrawn') {
        return 'ban'; // Ban icon for withdrawn licenses
      }
      if (eventType === 'license.granted') {
        return 'award'; // Award icon for granted licenses
      }
      if (eventType === 'license.extended') {
        return 'calendar-plus'; // Calendar plus icon for extended licenses
      }
      if (eventType === 'license.created') {
        return 'certificate'; // Certificate icon for new licenses
      }
      if (eventType === 'license.canceled') {
        return 'times-circle'; // X circle for canceled licenses
      }
      if (eventType.includes('license.extension_review')) {
        return 'clipboard-check'; // Clipboard check for review workflow events
      }

      // Default icons based on event type patterns
      if (eventType.includes('state_changed')) return 'arrows-turn-to-dots';
      if (eventType.includes('created')) return 'plus-circle';
      if (eventType.includes('updated')) return 'pen-to-square';
      if (eventType.includes('archived')) return 'inbox';
      if (eventType.includes('restored')) return 'undo';
      if (eventType.includes('completed')) return 'check-circle';
      if (eventType.includes('cancelled')) return 'times-circle';

      return 'info-circle'; // Default icon
    };

    // Custom color mapper for license-specific events
    const iconColorMapper = (eventType: string, _eventData?: unknown) => {
      // Specific colors for license events
      if (eventType === 'license.withdrawn') {
        return '#dc3545'; // Red for withdrawn licenses
      }
      if (eventType === 'license.granted') {
        return '#28a745'; // Green for granted licenses
      }
      if (eventType === 'license.extended') {
        return '#17a2b8'; // Cyan/teal for extended licenses
      }
      if (eventType === 'license.created') {
        return '#007bff'; // Blue for new licenses
      }
      if (eventType === 'license.canceled') {
        return '#dc3545'; // Red for canceled licenses
      }
      if (eventType === 'license.suspended') {
        return '#ffc107'; // Amber for suspended licenses
      }
      if (eventType === 'license.resumed') {
        return '#28a745'; // Green for resumed licenses
      }
      if (eventType.includes('license.extension_review')) {
        return '#ffc107'; // Yellow/amber for review workflow events
      }

      // Default colors based on event type patterns
      if (eventType.includes('state_changed')) return '#6c757d'; // Gray
      if (eventType.includes('created')) return '#007bff'; // Blue
      if (eventType.includes('updated')) return '#17a2b8'; // Cyan
      if (eventType.includes('archived')) return '#6c757d'; // Gray
      if (eventType.includes('restored')) return '#28a745'; // Green
      if (eventType.includes('completed')) return '#28a745'; // Green
      if (eventType.includes('cancelled')) return '#dc3545'; // Red

      return '#6c757d'; // Default gray
    };

    // Custom title mapper for better event descriptions
    const titleMapper = (eventType: string, _eventData?: unknown) => {
      const titleMap: Record<string, string> = {
        'license.withdrawn': 'License Withdrawn',
        'license.suspended': 'License Suspended',
        'license.resumed': 'License Resumed',
        'license.granted': 'License Granted',
        'license.extended': 'License Extended',
        'license.created': 'License Created',
        'license.updated': 'License Updated',
        'license.archived': 'License Archived',
        'license.restored': 'License Restored',
        'license.canceled': 'License Canceled',
        'license.extension_review.created': 'Extension Review Started',
        'license.extension_review.selection_confirmed': 'Extension Confirmed',
        'license.extension_review.certificates_sent': 'Certificates Sent',
        'license.extension_review.completed': 'Extension Review Completed',
        'license.extension_review.cancelled': 'Extension Review Cancelled',
      };

      return (
        titleMap[eventType] ||
        eventType
          .replace(/^[^.]+\./, '')
          .replace(/[._]/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase())
      );
    };

    this.dialogService.open(TimelineDialogComponent, {
      header: `Timeline - ${license.completeLicenseNumber}`,
      width: '600px',
      height: '100%',
      position: 'right',
      modal: true,
      draggable: false,
      closable: true,
      maskStyleClass: 'mask',
      contentStyle: { overflow: 'auto' },
      data: {
        entityId: license.id,
        entityType: 'License',
        entityName: license.completeLicenseNumber,
        timelineService: this.licenseTimelineService,
        config: {
          entityId: license.id.toString(),
          entityType: 'License',
          eventEntityTypes: ['License', 'CoaterLicense', 'LicenseRenewalWorkflow'],
          fetchNotes: false, // Licenses don't have notes yet
          fetchEvents: true,
          itemMappers: {
            iconMapper: iconMapper,
            titleMapper: titleMapper,
            eventMapper: (event: ApplicationEvent) => {
              // Return partial TimelineItem with custom iconColor
              return {
                iconColor: iconColorMapper(event.eventType, event.eventData),
              };
            },
            contentMapper: (eventType: string, eventData?: Record<string, unknown>) => {
              if (eventType === 'license.extended' && eventData) {
                const newExpiry = eventData['new_expiry'] as string;
                const extensionReason = eventData['extension_reason'] as string;
                if (newExpiry) {
                  const date = new Date(newExpiry);
                  const formattedDate = date.toLocaleDateString('en-GB');
                  let content = `License extended until ${formattedDate}`;
                  if (extensionReason) {
                    content += `\nReason: ${extensionReason}`;
                  }
                  return content;
                }
              }
              if (eventType === 'license.withdrawn' && eventData) {
                const reason = eventData['withdrawal_reason'] as string;
                if (reason) return `Reason: ${reason}`;
              }
              if (eventType === 'license.suspended' && eventData) {
                const reason = eventData['suspension_reason'] as string;
                if (reason) return `Reason: ${reason}`;
              }
              if (eventType === 'license.resumed' && eventData) {
                const reason = eventData['resumption_reason'] as string;
                if (reason) return `Reason: ${reason}`;
              }
              return '';
            },
          },
        },
      },
    });
  }

  navigateToLicenseRenewals() {
    this.router.navigate(['/certification/licenses/extension-reviews']);
  }

  openBulkDownloadDialog() {
    this.dialogService.open(LicenseBulkDownloadDialogComponent, {
      header: 'Bulk Download License Certificates',
      width: '500px',
      modal: true,
      closable: true,
      draggable: false,
      // The "all coaters by certification year" mode is admin-only; GL users only get the
      // per-coater download (scoped to their coaters by the API).
      data: { byYearAllowed: this.canManageRenewalWorkflows() },
    });
  }

  openShareCertificatesDialog() {
    this.dialogService.open(ShareCertificatesDialogComponent, {
      header: 'Share Certificates',
      width: '640px',
      modal: true,
      closable: true,
      draggable: false,
      data: { kind: 'licenses' },
    });
  }
}
